import React, { useMemo, useCallback } from 'react';
import { Steps, ConfigProvider } from 'antd';
import type { StepsProps as AntStepsProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

// Steps Semantic DOM 样式类型
interface StepsSemanticStyles {
  root?: React.CSSProperties;
  item?: React.CSSProperties;
  itemWrapper?: React.CSSProperties;
  itemIcon?: React.CSSProperties;
  itemHeader?: React.CSSProperties;
  itemTitle?: React.CSSProperties;
  itemSubtitle?: React.CSSProperties;
  itemSection?: React.CSSProperties;
  itemContent?: React.CSSProperties;
  itemRail?: React.CSSProperties;
}

// StepItem Semantic DOM 样式类型
interface StepItemSemanticStyles {
  root?: React.CSSProperties;
  wrapper?: React.CSSProperties;
  icon?: React.CSSProperties;
  header?: React.CSSProperties;
  title?: React.CSSProperties;
  subtitle?: React.CSSProperties;
  section?: React.CSSProperties;
  content?: React.CSSProperties;
  rail?: React.CSSProperties;
}

// 步骤项类型
interface StepItem {
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  content?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  status?: 'wait' | 'process' | 'finish' | 'error';
  styles?: StepItemSemanticStyles;
}

export interface AndStepsProps extends Omit<AntStepsProps, 'onChange' | 'direction' | 'labelPlacement' | 'items'>, BaseComponentProps, DataSetConfig {
  changeAction?: string;
  onChange?: (current: number) => void;
  /** @deprecated Use orientation instead */
  direction?: 'horizontal' | 'vertical';
  /** Orientation of steps */
  orientation?: 'horizontal' | 'vertical';
  /** @deprecated Use titlePlacement instead */
  labelPlacement?: 'horizontal' | 'vertical';
  /** Title placement */
  titlePlacement?: 'horizontal' | 'vertical';
  /** Steps items */
  items?: StepItem[] | string;
  /** Semantic DOM styles for Steps */
  styles?: StepsSemanticStyles | string;
  /** Design Token */
  token?: Record<string, any> | string;
}

// 解析 JSON 字符串
const parseJson = <T,>(value: T | string | undefined, defaultValue: T): T => {
  if (!value) return defaultValue;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return defaultValue;
    }
  }
  return value as T;
};

export const AndSteps: React.FC<AndStepsProps> = ({
  id, sessionId, develop, dataSet, dataSetField, changeAction, onChange, 
  current: propCurrent, items: propItems,
  direction, orientation,
  labelPlacement, titlePlacement,
  styles: propStyles,
  token: propToken,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const storeCurrent = useMemo(() => {
    if (dataSetKey && dataSetField) return getDataSet(dataSetKey, dataSetField);
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  const current = useMemo(() => storeCurrent ?? propCurrent ?? 0, [storeCurrent, propCurrent]);

  // 向后兼容：direction → orientation
  const computedOrientation = useMemo(() => {
    return orientation ?? direction;
  }, [orientation, direction]);

  // 向后兼容：labelPlacement → titlePlacement
  const computedTitlePlacement = useMemo(() => {
    return titlePlacement ?? labelPlacement;
  }, [titlePlacement, labelPlacement]);

  // 解析 items
  const items = useMemo(() => {
    return parseJson<StepItem[]>(propItems, []);
  }, [propItems]);

  // 解析 Semantic DOM styles
  const styles = useMemo(() => {
    return parseJson<StepsSemanticStyles>(propStyles, {});
  }, [propStyles]);

  // 解析 Design Token
  const token = useMemo(() => {
    return parseJson<Record<string, any>>(propToken, {});
  }, [propToken]);

  const handleChange = useCallback((value: number) => {
    if (onChange) { onChange(value); return; }
    if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, value);
    if (changeAction && sessionId) {
      addCommand(sessionId, createCommand(changeAction, value));
    }
  }, [onChange, dataSetKey, dataSetField, setDataSet, changeAction, sessionId, addCommand]);

  // 检查是否需要 ConfigProvider
  const hasToken = Object.keys(token).length > 0;

  const stepsElement = (
    <Steps 
      {...restProps} 
      current={current} 
      items={items}
      styles={styles}
      orientation={computedOrientation}
      titlePlacement={computedTitlePlacement}
      onChange={handleChange} 
    />
  );

  // 如果有 token 配置，使用 ConfigProvider 包装
  if (hasToken) {
    return (
      <ConfigProvider theme={{ components: { Steps: token } }}>
        {stepsElement}
      </ConfigProvider>
    );
  }

  return stepsElement;
};

export default AndSteps;
